import { useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { AgGridReact } from "ag-grid-react";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isNull, isUndefined, replace } from "lodash";
import NumberUtil from "@plannel/utils/number-util";
import locationService from "@plannel/services/data/location-service";
import materialOrderService from "@plannel/services/data/material-order-service";
import supplierService from "@plannel/services/data/supplier-service";
import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import GridDatePicker from "@plannel/components/GridDatePicker"
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import PaginationContainer from "@plannel/components/PaginationContainer";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import colorUtil from "../utils/color-util";
import ResponseUtil from "../utils/response-util";
import CodeUtil from "../utils/code-util";

const { snackBarMessageColor } = colorUtil;
const { httpStatusCode, responseCode } = ResponseUtil;
const CURRENT_DATE = new ZDate();
const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() - 5, 1);
const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const INIT_DATE = { start: START_DATE, end: END_DATE };
const PLANT = "PLANT";
const EXCEL_EXPORT = "excelExport";

const MaterialOrder = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const reduxState = reduxUtil.createObjectState(viewName);
  reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? null);
  const [supplierSearch, setSupplierSearch] = useState(reduxViewState?.supplierSearch ?? null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [period, setPeriod] = useState(reduxViewState?.start && reduxViewState?.end ? { start: reduxViewState.start, end: reduxViewState.end } : INIT_DATE);

  const [tPages, setTotalPages] = useState(0);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [materialOrders, setMaterialOrders] = useState([]);
  const [locationPlant, setLocationPlant] = useState(reduxViewState?.locationPlant ?? []);
  const [locations, setLocations] = useState(reduxViewState?.locations ?? []);
  const [suppliers, setSuppliers] = useState(reduxViewState?.suppliers ?? []);

  const userData = JSON.parse(localStorage.getItem("user"));

  const currencyRenderer = (param, isExport = false) => {
    let symbol = userData?.currencySymbol;
    const currencyCd = param?.data?.currencyCd;
    if (currencyCd) {
      symbol = CodeUtil.getCurrencySymbol(currencyCd);
    }
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};
    if (locationSearch) {
      searchFilters.locCd = locationSearch.locCd;
    }

    if (supplierSearch) {
      searchFilters.supplierCd = supplierSearch.supplierCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (period?.start) {
      searchFilters.startDate = period.start;
    }

    if (period?.end) {
      searchFilters.endDate = period.end;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  };

  const retrieveMaterialOrder = () => {
    const params = getRequestParams();
    gridRef.current?.api?.setGridOption('loading', true);
    materialOrderService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setMaterialOrders(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        if (reduxViewState) {
          reduxDispatch(reduxUtil.updateViewState({
            ...reduxState,
            ...reduxViewState,
            start: period?.start,
            end: period?.end,
            locationSearch,
            supplierSearch,
            itemSearch: itemSearch.current,
            extraItemCodes: extraItemCodesRef.current,
            currentPage,
            pageSize,
            advancedFilters,
          }));
        } 
      
        DataState.initialize(gridRef.current?.api);
      });
    gridRef.current?.api?.setGridOption('loading', false);
  };

  const retrieveLocations = () => {
    locationService.getLookup({ isLocationMaster: true })
      .then(response => {
        const { results } = response.data;
        const plant = [];

        for (const loc of results) {
          if (loc.locType === PLANT) {
            plant.push(loc);
          }
        }
        
        setLocationPlant(plant);
        setLocations(results);
        if (reduxViewState) {
          reduxViewState.locations = results;
          reduxViewState.locationPlant = plant;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      })
  };

  const retrieveSuppliers = () => {
    supplierService.getAll()
      .then(response => {
        const { results } = response.data;
        setSuppliers(results);
        if (reduxViewState) {
          reduxViewState.suppliers = results;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const removeMaterialOrder = () => {
    const materialOrderId = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        materialOrderId.push(data.id);
      }
    );

    if (materialOrderId.length > 0) {
      materialOrderService.remove(materialOrderId)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveMaterialOrder();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveMaterialOrder = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      for (const data of allStateData) {
        if (data.itemCd === null || data.itemCd === "") {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NoItemCd") });
          return;
        }

        if (data.dueDate === null || data.dueDate === "") {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NoDate") });
          return;
        }
      }
      materialOrderService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const status = e?.response?.status;
          const errorDatas = e?.response?.data;
          const { message } = errorDatas;

          if (status === httpStatusCode.BadRequest) {
            for (const errorCode of Object.keys(errorDatas)) {
              if (errorCode === responseCode.NOT_FOUND_ITEM) {
                setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotExistItemCodes", { itemCodes: errorDatas[errorCode] }) });
              } else if (errorCode === responseCode.INVALID_ITEM_TYPE) {
                setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ItemTypeCheck", { itemCodes: errorDatas[errorCode] }) });
              } else {
                setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
              }
              break;
            }
          } else if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveMaterialOrder();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveMaterialOrder();
    } else if (action === "DELETE") {
      removeMaterialOrder();
    }
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveMaterialOrder();
    }
  }

  const addRow = () => {
    const newRow = {
      id: null,
      locCd: "",
      itemCd: "",
      supplierCd: "",
      baseDate: "",
      dueDate: ""
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  // const locationKeyCreator = (params) => {
  //   return params.value.locCd;
  // }
  //
  // const customerKeyCreator = (params) => {
  //   return params.value.customerCd;
  // }

  const dropdownValueFormatter = (data) => {
    return data.value ? data.value.name : "";
  }

  const objectComparator = (value1, value2, isDescending) => {
    const val1 = !value1 ? "" : value1.name;
    const val2 = !value2 ? "" : value2.name;
    
    return nullSafeComparator(val1, val2, isDescending);
  };

  const setItemCode = function (params) {
    params.data.item = { itemCd: params.newValue, name: "" };
    params.data.itemCd = params.newValue;
    params.data.supplier = null;
    params.data.supplierCd = null;
    params.data.supplierName = null;
    return true;
  }

  const columnDefs = [
    { headerName: "locCd", field: "location.locCd", colId: "locCd", minWidth: 150, type: ["nonEditableColumn"], flex: 2, cellClass: 'stringType', filterType: "string" },
    {
      headerName: "locName", field: "location", colId: "locName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        formatValue: (value) => value ? value.name : "",
        values: locationPlant
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, isDescending),
      minWidth: 200,
      flex: 2,
      filterType: "string",
    },
    { headerName: "itemCd", field: "item.itemCd", colId: "itemCd", valueSetter: setItemCode, width: 150, cellClass: 'stringType', filterType: "string" },
    { headerName: "itemName", field: "item.name", colId: "itemName", type: ["nonEditableColumn"], width: 280, filterType: "string" },
    { headerName: "supplierCd", field: "supplier.supplierCd", colId: "supplierCd", type: ["nonEditableColumn"], cellClass: 'stringType', minWidth: 150, flex: 2, filterType: "string" },
    {
      headerName: "supplierName", field: "supplier.name", colId: "supplierName",
      type: ["nonEditableColumn"],
      minWidth: 150,
      flex: 2,
      filterType: "string",
    },
    { headerName: "baseDt", field: "baseDate",
      cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" },
      valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd"), width: 100, filterType: "date" },
    { headerName: "dueDate", field: "dueDate",
      type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" },
      valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd"), width: 100, filterType: "date" },
    { headerName: "demandQty", field: "orderQty", type: ["numberColumn", "rightAligned"], width: 120, filterType: "number" },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 4, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 4, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (reduxViewState && gridLoading && period) {
      DataState.initialize(gridRef.current?.api);
      retrieveMaterialOrder();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearch, supplierSearch, period, itemSearch.current, extraItemCodesRef.current, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(locations)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations])

  useEffect(() => {
    if (!isEmpty(suppliers)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers])

  useEffect(() => {
    setGridLoading(true);
    if (!reduxViewState?.locations) {
      retrieveLocations();
    }
    if (!reduxViewState?.suppliers) {
      retrieveSuppliers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <PeriodFilter period={period} setPeriod={setPeriod} dateType="month" />
        <Autocomplete
          options={suppliers}
          getOptionLabel={option => option.name}
          value={supplierSearch}
          onChange={(_, newValue) => setSupplierSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.supplierCd === value.supplierCd}
          size="small"
          sx={{ minWidth: 190, mr: 1 }}
          renderInput={(params) => (
            <TextField {...params} label={t("supplier")} />
          )}
        />
        <Autocomplete
          options={locationPlant}
          getOptionLabel={option => option.name}
          value={locationSearch}
          onChange={(_, newValue) => setLocationSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ minWidth: 240, mr: 1 }}
          renderInput={(params) => (
            <TextField {...params} label={t("location")} />
          )}
        />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={materialOrderService} columnDefs={columnDefs}
            params={getRequestParams()} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="material-order-grid" className="ag-theme-balham grid-area" sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={materialOrders}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              enterNavigatesVertically
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(MaterialOrder);